// src/pages/CustomerReport/CustomerReport.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import CustomersFilters from '../../components/CustomerFilters';
import CustomersDataGrid from '../../components/CustomerDataGrid';
import { Typography, CircularProgress, Box } from '@mui/material';
import BackButton from '../../components/BackButton';

const CustomerReport = () => {
  const { reportType } = useParams();
  const navigate = useNavigate();

  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userModules, setUserModules] = useState([]);
  const [privileges, setPrivileges] = useState({});
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [moduleId, setModuleId] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [noCustomersFound, setNoCustomersFound] = useState(false);
  const [privilegesLoaded, setPrivilegesLoaded] = useState(false); // NEW: Track if privileges are loaded
  const [accessChecked, setAccessChecked] = useState(false); // NEW: Track if access has been checked

  /* ───────────────────────────────────────────────
   *  Helpers
   * ───────────────────────────────────────────── */
  const getModuleIdByReportType = (type) => {
    switch (type) {
      case 'transactions':
        return 3810;
      case 'rewards':
        return 3811;
      case 'balance':
        return 3812;
      case 'games':
        return 3813;
      case 'welfare-transactions':
        return 3814;
      case 'handle-welfare':
        return 3815;
      case 'vpa-search':
        return 3816;
      case 'mobile-search':
        return 3817;
      default:
        return null;
    }
  };

  const getReportTitle = (type) => {
    switch (type) {
      case 'transactions':
        return 'Customer Transactions';
      case 'rewards':
        return 'Customer Rewards';
      case 'balance':
        return 'Customer Balance';
      case 'games':
        return 'Customer Games';
      case 'welfare-transactions':
        return 'Welfare Transactions';
      case 'handle-welfare':
        return 'Handle Welfare';
      case 'vpa-search':
        return 'VPA Search';
      case 'mobile-search':
        return 'Mobile Search';
      default:
        return 'Customer Report';
    }
  };

  const hasAccess = () => {
    if (userLevel === 1 || userLevel === 2) {
      return true;
    }
    if (privilegesLoaded && Array.isArray(userModules)) {
      return userModules.includes(moduleId);
    }
    return null;
  };

  /* ───────────────────────────────────────────────
   *  Enhanced Privileges Function
   * ───────────────────────────────────────────── */
  const fetchUserPrivileges = async (uid) => {
    try {
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        console.error('No token available for privileges request');
        navigate('/login');
        return;
      }
      
      const { data, status } = await axios.get(
        `${API_BASE_URL}/privileges/modules/${uid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (status === 200 && Array.isArray(data.modules)) {
        setUserModules(data.modules);
        setPrivileges(data);
        setPrivilegesLoaded(true); // Mark privileges as loaded
      } else {
        console.warn('Invalid privileges response structure:', data);
        setUserModules([]);
        setPrivilegesLoaded(true); // Still mark as loaded even if empty
      }
    } catch (err) {
      console.error('Error fetching user privileges:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        console.error('Unauthorized - token might be expired');
        sessionStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('Forbidden - user does not have access');
        setPrivilegesLoaded(true); // Mark as loaded so access check can proceed
        navigate('/unauthorized');
      } else {
        console.error('Network or server error:', err.message);
        setPrivilegesLoaded(true); // Mark as loaded so access check can proceed
      }
      
      setUserModules([]);
    }
  };

  /* ───────────────────────────────────────────────
   *  Enhanced Token Decode & Initialize
   * ───────────────────────────────────────────── */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const decoded = jwtDecode(token);

      if (!decoded.level || !decoded.userId) {
        sessionStorage.removeItem('token');
        navigate('/login');
        return;
      }
      
      setUserLevel(decoded.level);
      setUserId(decoded.userId);

      const modId = getModuleIdByReportType(reportType);

      if (!modId) {
        navigate('/unauthorized');
        return;
      }

      setModuleId(modId);

      // For admin levels, set modules immediately and mark privileges as loaded
      if (decoded.level === 1 || decoded.level === 2) {
        setUserModules([modId]);
        setPrivilegesLoaded(true);
      } else {
        fetchUserPrivileges(decoded.userId);
      }
      
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      console.error('This usually means the token is malformed or expired');
      
      sessionStorage.removeItem('token');
      alert('Your session has expired. Please login again.');
      navigate('/login');
    }
  }, [reportType, navigate]);

  /* ───────────────────────────────────────────────
   *  Access Check - Only run when privileges are loaded
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (!moduleId || !privilegesLoaded || accessChecked) return;

    const access = hasAccess();

    if (access === null) {
      return;
    }

    if (!access) {
      navigate('/unauthorized');
    } else {
      setReportTitle(getReportTitle(reportType));
      setAccessChecked(true); // Mark access as checked
    }
  }, [moduleId, privilegesLoaded, userModules, userLevel, reportType, navigate, accessChecked]);

  /* ───────────────────────────────────────────────
   *  Fetch customers
   * ───────────────────────────────────────────── */
  const fetchCustomers = async (flt) => {
    setLoading(true);
    try {
      const pageNumber = flt.pageNumber || 1;
      const token = sessionStorage.getItem('token');

      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageNumber, limit: flt.limit || 100 },
      };

      let url = `${API_BASE_URL}/customerProfiles`;

      if (flt.pincode) {
        url = `${API_BASE_URL}/customerProfiles/byPincode`;
        config.params.pincode = flt.pincode;
      } else if (flt.talukId) {
        url = `${API_BASE_URL}/customerProfiles/byTaluk`;
        config.params.talukId = flt.talukId;
      } else if (flt.districtId) {
        url = `${API_BASE_URL}/customerProfiles/byDistrict`;
        config.params.districtId = flt.districtId;
      }

      const { data, status } = await axios.get(url, config);

      if (status === 200 && data.customers?.length) {
        setCustomers(data.customers);
        setNoCustomersFound(false);
      } else {
        setCustomers([]);
        setNoCustomersFound(true);
      }
    } catch (err) {
      setCustomers([]);
      setNoCustomersFound(true);
      console.error('Error fetching customers:', err);
    }
    setLoading(false);
  };

  // Only fetch customers when access is confirmed
  useEffect(() => {
    if (userId !== null && accessChecked && privilegesLoaded) {
      fetchCustomers(filters);
    }
  }, [filters, userId, accessChecked, privilegesLoaded]);

  /* ───────────────────────────────────────────────
   *  Render - Show loading until access is checked
   * ───────────────────────────────────────────── */
  if (!privilegesLoaded || !accessChecked) {
    return (
      <Box sx={{ position: 'relative', padding: 2, textAlign: 'center', mt: 4 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading privileges...
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br />
        <br />
        <Typography variant="h6" sx={{ mt: 1 }}>
          {reportTitle}
        </Typography>

        <CustomersFilters
          userLevel={userLevel}
          privileges={privileges}
          setFilters={setFilters}
          hasAccess={() => true} // Access already verified
        />

        {loading ? (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box sx={{ mt: 2, height: 600, width: '100%' }}>
            {customers.length ? (
              <CustomersDataGrid
                customers={customers}
                loading={loading}
                reportType={reportType}
                moduleId={moduleId}
              />
            ) : (
              <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                {noCustomersFound
                  ? 'No customers found for the selected filters.'
                  : 'Use filters to load customers.'}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </div>
  );
};

export default CustomerReport;